import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import APIClient from '../services/apiClient';
import { fetchMe } from '../services/auth/meService';
import { mergeGuestCartIntoAccount } from '../services/cart/cartService';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    const u = await fetchMe();
    setUser(u);
    setLoading(false);
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Non-secret marker so plain service modules (e.g. cart) can tell guest vs
  // logged-in synchronously. NOT a credential — the real token lives only in the
  // httpOnly cookie. Derived from user so hydrate/login/logout stay in sync.
  useEffect(() => {
    // Don't touch the flag until hydration resolves, or we'd clear it on every
    // mount (user starts null) and briefly read a logged-in user as a guest.
    if (loading) return;
    try {
      if (user) localStorage.setItem('loggedIn', '1');
      else localStorage.removeItem('loggedIn');
    } catch (e) { /* localStorage unavailable */ }
  }, [user, loading]);

  const login = useCallback(async (credentials) => {
    try {
      const res = await APIClient.post('/authentication/user/login', credentials);
      if (res.data?.success) {
        // Set the flag synchronously: the [user]-keyed effect above won't run
        // until refresh() below sets user and React re-renders, which is too
        // late for the merge call right after this. Without this, cartService's
        // isLoggedIn() would still read false during the merge and no-op it —
        // the exact bug this flag exists to fix.
        try { localStorage.setItem('loggedIn', '1'); } catch (e) { /* localStorage unavailable */ }
        // Carry anything added while browsing as a guest over to the account.
        // Guarded: a cart hiccup must never fail an otherwise-successful login.
        try { await mergeGuestCartIntoAccount(); } catch (e) { /* left for a later login */ }
        await refresh();
        return { success: true };
      }
      return { success: false, message: res.data?.message || 'Invalid email or password' };
    } catch (e) {
      return { success: false, message: e.response?.data?.message || 'Invalid email or password' };
    }
  }, [refresh]);

  const logout = useCallback(async () => {
    try { await APIClient.post('/authentication/logout'); } catch (e) { /* clear locally regardless */ }
    try { localStorage.removeItem('loggedIn'); } catch (e) { /* ignore */ }
    setUser(null);
  }, []);

  const value = { user, isLoggedIn: !!user, loading, refresh, login, logout };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
